import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  dateToActivityFromInstant,
  dateToActivityToInstantExclusive,
  useGetUserActivityEventsQuery,
  useGetUserActivityRecentQuery,
} from "../store";

const EVENTS_PAGE_SIZE = 25;

const EVENT_ACTION_FILTER_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "VIEW", label: "View" },
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
  { value: "EXPORT", label: "Export" },
  { value: "RUN", label: "Run" },
];

const ACTIVITY_FIELD_LABELS = {
  contractName: "Contract Name",
  subContract: "Sub Contract",
  contractVersion: "Contract Version",
  effectiveDate: "Effective Date",
  endDate: "End Date",
  status: "Status",
  billingCycle: "Billing Cycle",
  netTerms: "Net Terms",
  uploadedDocument: "Uploaded Document",
  notes: "Notes",
  category: "Category",
  subCategory: "Sub Category",
  serviceCode: "Service Code",
  description: "Description",
  glCode: "GL Code",
  unitOfMeasure: "Unit of Measure",
  costSheetRate: "Cost Sheet Rate",
  servicesRendered: "Services Rendered",
  basedOn: "Based On",
  defaultInvoiceDescription: "Default Invoice Description",
  sequenceDisplay: "Display Sequence",
  lineDescription: "Line Description",
  invoiceId: "Invoice ID",
  invoiceNumber: "Invoice Number",
  invoiceLineId: "Invoice Line ID",
  invProfileDetailId: "Profile Detail ID",
  quantity: "Quantity",
  amount: "Amount",
  adjustment: "Adjustment",
  suppressed: "Suppressed",
  overrideFlag: "Override",
  overrideAmount: "Override Amount",
  overrideReason: "Override Reason",
  overrideSetOn: "Override Set On",
  overrideQuantity: "Override Quantity",
  overrideQuantityReason: "Override Quantity Reason",
  overrideQuantitySetOn: "Override Quantity Set On",
  clientRate: "Client Rate",
  tieredPricingLov: "Tiered Pricing",
  tieredPricing: "Tiered Pricing Tiers",
  profileName: "Profile Name",
  clientName: "Client",
  clientCode: "Client Code",
  invoiceNameProgram: "Invoice Name / Program",
  companyNumbers: "Company Numbers",
  poId: "PO ID",
  invPoNum: "PO Number",
  jobNos: "Assigned Jackets",
  activeFlag: "Status",
  contractServiceId: "Contract Service ID",
  billingDataClassifications: "Billing Classifications",
  opCodes: "Op Codes",
  trigger: "Trigger",
  runDate: "Run Date",
  serviceMonth: "Service Month",
  profilesCreated: "Invoices Created",
  profilesSkipped: "Profiles Skipped",
  profilesFailed: "Profiles Failed",
};

function formatChangeValue(value) {
  if (value == null) {
    return "—";
  }
  const text = String(value).trim();
  return text === "" ? "—" : text;
}

function getEventChanges(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return [];
  }
  const changes = metadataJson.changes;
  return Array.isArray(changes) ? changes : [];
}

function getMetadataContractName(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(metadataJson.contractName ?? "").trim();
}

function getMetadataServiceName(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(metadataJson.serviceName ?? metadataJson.servicesRendered ?? "").trim();
}

function getMetadataProfileName(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(metadataJson.profileName ?? metadataJson.invoiceNameProgram ?? "").trim();
}

function getMetadataLineDescription(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(
    metadataJson.lineDescription ?? metadataJson.defaultInvoiceDescription ?? ""
  ).trim();
}

function formatFieldLabel(field) {
  const key = String(field ?? "").trim();
  if (!key) {
    return "Field";
  }
  return ACTIVITY_FIELD_LABELS[key] ?? key;
}

const EVENT_TYPE_LABELS = {
  INV_PROFILE_SAVE: "Add invoice profile",
  INV_PROFILE_DEACTIVATE: "Deactivate invoice profile",
  INV_PROFILE_DETAIL_SAVE: "Add invoice line",
  INV_PROFILE_DETAIL_UPDATE: "Update invoice line",
  INV_PROFILE_DETAIL_END: "End invoice line",
  CLIENT_INVOICE_LINE_SAVE: "Add draft invoice line",
  CLIENT_INVOICE_LINE_UPDATE: "Update draft invoice line",
  CLIENT_INVOICE_LINE_DELETE: "Delete draft invoice line",
  CONTRACT_SAVE: "Add contract",
  CONTRACT_UPDATE: "Update contract",
  CONTRACT_DELETE: "Delete contract",
  CONTRACT_SERVICE_SAVE: "Add contract service",
  CONTRACT_SERVICE_UPDATE: "Update contract service",
  CONTRACT_SERVICE_DELETE: "Delete contract service",
  CONTRACT_SERVICE_END: "End contract service",
  SERVICE_CATALOG_SAVE: "Add offer",
  SERVICE_CATALOG_UPDATE: "Update offer",
  SERVICE_CATALOG_DELETE: "Deactivate offer",
  MONTHLY_INVOICE_JOB_RUN: "Run automated invoice job",
};

function formatEventTypeLabel(eventType) {
  const key = String(eventType ?? "").trim();
  if (!key) {
    return "—";
  }
  return EVENT_TYPE_LABELS[key] ?? key;
}

function getMetadataServiceCode(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(metadataJson.serviceCode ?? "").trim();
}

function getMetadataOfferDescription(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  return String(metadataJson.description ?? "").trim();
}

function formatAccordionContextLabel(metadataJson) {
  const contractName = getMetadataContractName(metadataJson);
  const profileName = getMetadataProfileName(metadataJson);
  const serviceName = getMetadataServiceName(metadataJson);
  const lineDescription = getMetadataLineDescription(metadataJson);
  const serviceCode = getMetadataServiceCode(metadataJson);
  const offerDescription = getMetadataOfferDescription(metadataJson);
  const parts = [];
  if (contractName) {
    parts.push(`Contract: ${contractName}`);
  }
  if (profileName) {
    parts.push(`Profile: ${profileName}`);
  }
  if (lineDescription) {
    parts.push(`Line: ${lineDescription}`);
  }
  if (serviceName) {
    parts.push(`Service: ${serviceName}`);
  }
  if (serviceCode) {
    parts.push(`Code: ${serviceCode}`);
  }
  if (offerDescription) {
    parts.push(`Description: ${offerDescription}`);
  }
  return parts.join(" · ");
}

function formatAccordionActionLabel(action) {
  const normalized = String(action ?? "").trim().toUpperCase();
  if (normalized === "CREATE") {
    return "Created";
  }
  if (normalized === "DELETE") {
    return "Deleted";
  }
  return "Changes";
}

function formatDateTime(iso) {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatLoadError(err, fallback) {
  if (!err) {
    return fallback;
  }
  if (err.status === 403) {
    return "You do not have permission to view this data.";
  }
  const data = err?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `${fallback} (HTTP ${err.status}).`;
  }
  return fallback;
}

function formatEventUser(row) {
  const metadata = row.metadataJson;
  if (
    metadata &&
    typeof metadata === "object" &&
    String(metadata.trigger ?? "").trim().toUpperCase() === "SCHEDULED"
  ) {
    const name = String(row.userName ?? "").trim();
    if (!name && (row.empId == null || row.empId === "")) {
      return "System (scheduled)";
    }
  }
  const name = String(row.userName ?? "").trim();
  if (name) {
    return name;
  }
  if (row.empId != null && row.empId !== "") {
    return `Employee #${row.empId}`;
  }
  return "—";
}

function getMetadataJobSummary(metadataJson) {
  if (!metadataJson || typeof metadataJson !== "object") {
    return "";
  }
  const trigger = String(metadataJson.trigger ?? "").trim();
  const serviceMonth = String(metadataJson.serviceMonth ?? "").trim();
  const runDate = String(metadataJson.runDate ?? "").trim();
  const created = metadataJson.profilesCreated;
  const skipped = metadataJson.profilesSkipped;
  const failed = metadataJson.profilesFailed;
  const parts = [];
  if (trigger) {
    parts.push(trigger === "SCHEDULED" ? "Scheduled" : "Manual");
  }
  if (serviceMonth) {
    parts.push(`Service month ${serviceMonth}`);
  } else if (runDate) {
    parts.push(`Run date ${runDate}`);
  }
  if (created != null || skipped != null || failed != null) {
    parts.push(`Created ${created ?? 0}, skipped ${skipped ?? 0}, failed ${failed ?? 0}`);
  }
  return parts.join(" · ");
}

function formatEventResource(row) {
  const type = String(row.resourceType ?? "").trim();
  const id = String(row.resourceId ?? "").trim();
  const action = String(row.action ?? "").trim();
  const jobSummary =
    type === "MONTHLY_INVOICE_JOB" ? getMetadataJobSummary(row.metadataJson) : "";
  const contractName =
    type === "CONTRACT" || type === "CONTRACT_SERVICE"
      ? getMetadataContractName(row.metadataJson)
      : "";
  const profileName =
    type === "INV_PROFILE" || type === "INV_PROFILE_DETAIL"
      ? getMetadataProfileName(row.metadataJson)
      : "";
  const lineDescription =
    type === "CLIENT_INVOICE_LINE" ? getMetadataLineDescription(row.metadataJson) : "";
  const offerDescription =
    type === "SERVICE_CATALOG" ? getMetadataOfferDescription(row.metadataJson) : "";
  const serviceCatalogCode =
    type === "SERVICE_CATALOG"
      ? getMetadataServiceCode(row.metadataJson) || id
      : "";
  const serviceName =
    type === "CONTRACT_SERVICE" || type === "INV_PROFILE_DETAIL"
      ? getMetadataServiceName(row.metadataJson)
      : "";
  if (
    !type &&
    !id &&
    !action &&
    !jobSummary &&
    !contractName &&
    !profileName &&
    !lineDescription &&
    !serviceName &&
    !offerDescription &&
    !serviceCatalogCode
  ) {
    return "—";
  }
  let text = jobSummary || type || "";
  if (lineDescription) {
    text = text ? `${text} — ${lineDescription}` : lineDescription;
  } else if (offerDescription) {
    text = text ? `${text} — ${offerDescription}` : offerDescription;
  } else if (serviceCatalogCode) {
    text = text ? `${text} — ${serviceCatalogCode}` : serviceCatalogCode;
  } else if (serviceName) {
    text = text ? `${text} — ${serviceName}` : serviceName;
  } else if (profileName) {
    text = text ? `${text} — ${profileName}` : profileName;
  } else if (contractName) {
    text = text ? `${text} — ${contractName}` : contractName;
  } else if (id) {
    text = text ? `${text} ${id}` : id;
  }
  if (action) {
    text = text ? `${text} (${action})` : action;
  }
  return text;
}

function EventChangesAccordion({ metadataJson, action }) {
  const [expanded, setExpanded] = useState(false);
  const changes = getEventChanges(metadataJson);
  const contextLabel = formatAccordionContextLabel(metadataJson);
  const actionLabel = formatAccordionActionLabel(action);
  if (changes.length === 0) {
    return null;
  }
  return (
    <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="app-btn-plain w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
        aria-expanded={expanded}
      >
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {contextLabel ? (
            <>
              <span className="text-gray-600 dark:text-gray-400 font-normal">{contextLabel}</span>
              <span className="text-gray-600 dark:text-gray-400 font-normal">
                {" "}
                · {actionLabel} ({changes.length})
              </span>
            </>
          ) : (
            `${actionLabel} (${changes.length})`
          )}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
        )}
      </button>
      {expanded ? (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-white dark:bg-gray-900">
          <ul className="space-y-2">
            {changes.map((change, index) => {
              const fieldKey = `${String(change?.field ?? "field")}-${index}`;
              return (
                <li
                  key={fieldKey}
                  className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800/60"
                >
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatFieldLabel(change?.field)}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {": "}
                    {formatChangeValue(change?.old)}
                    {" → "}
                    {formatChangeValue(change?.new)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function EventSuccessBadge({ successFlag }) {
  return (
    <span
      className={`inline-flex px-2 py-1 rounded text-xs ${
        successFlag
          ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
          : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
      }`}
    >
      {successFlag ? "Yes" : "No"}
    </span>
  );
}

function EventHistoryDetailCard({ row, highlighted = false }) {
  const contractName = getMetadataContractName(row.metadataJson);
  const profileName = getMetadataProfileName(row.metadataJson);
  const lineDescription = getMetadataLineDescription(row.metadataJson);
  const serviceName = getMetadataServiceName(row.metadataJson);
  return (
    <div
      className={
        highlighted
          ? "rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-950/30"
          : ""
      }
    >
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Time
          </dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
            {formatDateTime(row.eventAt)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            User
          </dt>
          <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
            {formatEventUser(row)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Event type
          </dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
            {formatEventTypeLabel(row.eventType)}
          </dd>
        </div>
        <div className="sm:col-span-2 lg:col-span-2">
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Page path
          </dt>
          <dd className="mt-0.5 text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
            {row.pagePath || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Resource
          </dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{formatEventResource(row)}</dd>
        </div>
        {contractName ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Contract name
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{contractName}</dd>
          </div>
        ) : null}
        {profileName ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Profile name
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{profileName}</dd>
          </div>
        ) : null}
        {lineDescription ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Line description
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{lineDescription}</dd>
          </div>
        ) : null}
        {serviceName ? (
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Service name
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{serviceName}</dd>
          </div>
        ) : null}
        <div>
          <dt className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Success
          </dt>
          <dd className="mt-0.5">
            <EventSuccessBadge successFlag={row.successFlag} />
          </dd>
        </div>
      </dl>
      <EventChangesAccordion metadataJson={row.metadataJson} action={row.action} />
    </div>
  );
}

function EventHistoryTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Time
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Event type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Page path
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Resource
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Success
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
          {rows.map((row) => {
            const hasChanges = getEventChanges(row.metadataJson).length > 0;
            return (
              <Fragment key={row.activityEventId}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {formatDateTime(row.eventAt)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatEventUser(row)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {formatEventTypeLabel(row.eventType)}
                  </td>
                  <td className="px-6 py-4 text-xs font-mono text-gray-600 dark:text-gray-400">
                    {row.pagePath || "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {formatEventResource(row)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <EventSuccessBadge successFlag={row.successFlag} />
                  </td>
                </tr>
                {hasChanges ? (
                  <tr className="bg-gray-50/80 dark:bg-gray-800/40">
                    <td colSpan={6} className="px-6 pb-4 pt-0">
                      <EventChangesAccordion metadataJson={row.metadataJson} action={row.action} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UserLoginHistorySection() {
  const { data: users = [], isLoading, isError, error, refetch } = useGetUserActivityRecentQuery(50, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User Login History</h2>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isLoading}
          className="app-btn-gloss text-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {isLoading ? (
        <p className="p-6 text-sm text-gray-600 dark:text-gray-400" role="status">
          Loading user activity…
        </p>
      ) : null}

      {isError ? (
        <p className="p-6 text-sm text-red-600 dark:text-red-400" role="alert">
          {formatLoadError(error, "Unable to load user activity.")}
        </p>
      ) : null}

      {!isLoading && !isError && users.length === 0 ? (
        <p className="p-6 text-sm text-gray-600 dark:text-gray-400" role="status">
          No login history found yet. Users will appear here after they sign in.
        </p>
      ) : null}

      {!isLoading && !isError && users.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Emp ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Last Login
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {users.map((user) => {
                const roleLabel = String(user.role ?? "").trim() || "—";
                const isAdminRole =
                  roleLabel.toLowerCase().includes("administrator") ||
                  String(user.role ?? "").toUpperCase() === "ACCT_ADMIN";
                return (
                  <tr key={user.empId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user.name || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 tabular-nums">
                      {user.empId ?? "—"}
                    </td>
                    <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400">
                      {user.email || "—"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          isAdminRole
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        }`}
                      >
                        {roleLabel}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {formatDateTime(user.lastLogin)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function EventHistorySection() {
  const [draftEmpId, setDraftEmpId] = useState("");
  const [draftPagePath, setDraftPagePath] = useState("");
  const [draftAction, setDraftAction] = useState("");
  const [draftFromDate, setDraftFromDate] = useState("");
  const [draftToDate, setDraftToDate] = useState("");
  const [earlierExpanded, setEarlierExpanded] = useState(false);
  const [query, setQuery] = useState({
    page: 1,
    empId: "",
    pagePath: "",
    action: "",
    fromDate: "",
    toDate: "",
  });

  const filterArgs = useMemo(() => {
    const empIdRaw = query.empId.trim();
    const empIdParsed = Number(empIdRaw);
    const pagePathRaw = query.pagePath.trim();
    const actionRaw = query.action.trim();
    return {
      empId:
        empIdRaw === "" || !Number.isFinite(empIdParsed) ? undefined : empIdParsed,
      pagePath: pagePathRaw === "" ? undefined : pagePathRaw,
      action: actionRaw === "" ? undefined : actionRaw,
      from: dateToActivityFromInstant(query.fromDate),
      to: dateToActivityToInstantExclusive(query.toDate),
    };
  }, [query.empId, query.pagePath, query.action, query.fromDate, query.toDate]);

  const latestQueryArgs = useMemo(
    () => ({ page: 1, size: 1, ...filterArgs }),
    [filterArgs]
  );

  const listQueryArgs = useMemo(
    () => ({ page: query.page, size: EVENTS_PAGE_SIZE, ...filterArgs }),
    [query.page, filterArgs]
  );

  const {
    data: latestData,
    isLoading: isLatestLoading,
    isFetching: isLatestFetching,
    isError: isLatestError,
    error: latestError,
    refetch: refetchLatest,
  } = useGetUserActivityEventsQuery(latestQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: listData,
    isLoading: isListLoading,
    isFetching: isListFetching,
    isError: isListError,
    error: listError,
    refetch: refetchList,
  } = useGetUserActivityEventsQuery(listQueryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const isLoading = isLatestLoading || isListLoading;
  const isFetching = isLatestFetching || isListFetching;
  const isError = isLatestError || isListError;
  const error = isLatestError ? latestError : listError;

  const refetch = () => {
    refetchLatest();
    refetchList();
  };

  const latestEvent = latestData?.items?.[0] ?? null;
  const latestEventId = latestEvent?.activityEventId;
  const page = listData?.page ?? query.page;
  const totalFiltered = listData?.totalFiltered ?? 0;
  const earlierCount = Math.max(0, totalFiltered - (latestEvent ? 1 : 0));

  const olderItems = useMemo(() => {
    const items = listData?.items ?? [];
    if (latestEventId == null) {
      return items;
    }
    return items.filter((row) => row.activityEventId !== latestEventId);
  }, [listData?.items, latestEventId]);

  const totalEarlierPages = Math.max(1, Math.ceil(earlierCount / EVENTS_PAGE_SIZE));
  const earlierRangeStart =
    earlierCount === 0 ? 0 : (page - 1) * EVENTS_PAGE_SIZE + 1;
  const earlierRangeEnd = Math.min(page * EVENTS_PAGE_SIZE, earlierCount);

  useEffect(() => {
    setEarlierExpanded(false);
  }, [query.empId, query.pagePath, query.action, query.fromDate, query.toDate]);

  const applyFilters = () => {
    setQuery({
      page: 1,
      empId: draftEmpId.trim(),
      pagePath: draftPagePath.trim(),
      action: draftAction.trim(),
      fromDate: draftFromDate,
      toDate: draftToDate,
    });
  };

  const clearFilters = () => {
    setDraftEmpId("");
    setDraftPagePath("");
    setDraftAction("");
    setDraftFromDate("");
    setDraftToDate("");
    setQuery({ page: 1, empId: "", pagePath: "", action: "", fromDate: "", toDate: "" });
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm mt-8">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Event History</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Recent application events with filters by employee, page path, action, and date range
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isLoading || isFetching}
            className="app-btn-gloss text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Employee ID
            </span>
            <input
              type="number"
              min={1}
              value={draftEmpId}
              onChange={(e) => setDraftEmpId(e.target.value)}
              placeholder="All"
              className="mt-1 block w-36 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
            />
          </label>
          <label className="block w-36">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Action
            </span>
            <select
              value={draftAction}
              onChange={(e) => setDraftAction(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
            >
              {EVENT_ACTION_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block flex-1 min-w-[12rem] max-w-md">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Page path
            </span>
            <input
              type="search"
              value={draftPagePath}
              onChange={(e) => setDraftPagePath(e.target.value)}
              placeholder="e.g. /clients or invoices"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm font-mono"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              From date
            </span>
            <input
              type="date"
              value={draftFromDate}
              onChange={(e) => setDraftFromDate(e.target.value)}
              className="mt-1 block px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              To date
            </span>
            <input
              type="date"
              value={draftToDate}
              onChange={(e) => setDraftToDate(e.target.value)}
              className="mt-1 block px-3 py-2 border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={applyFilters}
              className="app-btn-gloss text-sm"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="app-btn-gloss text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <p className="p-6 text-sm text-gray-600 dark:text-gray-400" role="status">
          Loading events…
        </p>
      ) : null}

      {isError ? (
        <p className="p-6 text-sm text-red-600 dark:text-red-400" role="alert">
          {formatLoadError(error, "Unable to load event history.")}
        </p>
      ) : null}

      {!isLoading && !isError && !latestEvent ? (
        <p className="p-6 text-sm text-gray-600 dark:text-gray-400" role="status">
          No events match the current filters.
        </p>
      ) : null}

      {!isLoading && !isError && latestEvent ? (
        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Most recent activity
            </h3>
            <EventHistoryDetailCard row={latestEvent} highlighted />
          </div>

          {earlierCount > 0 ? (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setEarlierExpanded((open) => !open)}
                className="app-btn-plain w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left text-gray-900 dark:text-gray-100"
                aria-expanded={earlierExpanded}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Earlier activity ({earlierCount.toLocaleString()})
                </span>
                {earlierExpanded ? (
                  <ChevronUp className="w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 shrink-0 text-gray-500 dark:text-gray-400" />
                )}
              </button>

              {earlierExpanded ? (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  {olderItems.length === 0 && !isListLoading ? (
                    <p className="p-4 text-sm text-gray-600 dark:text-gray-400">
                      No additional events on this page.
                    </p>
                  ) : (
                    <EventHistoryTable rows={olderItems} />
                  )}

                  {earlierCount > EVENTS_PAGE_SIZE ? (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        Showing {earlierRangeStart}–{earlierRangeEnd} of {earlierCount} earlier
                        events
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={page <= 1 || isFetching}
                          onClick={() =>
                            setQuery((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
                          }
                          className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <span className="px-2">
                          Page {page} of {totalEarlierPages}
                        </span>
                        <button
                          type="button"
                          disabled={page >= totalEarlierPages || isFetching}
                          onClick={() =>
                            setQuery((prev) => ({
                              ...prev,
                              page: Math.min(totalEarlierPages, prev.page + 1),
                            }))
                          }
                          className="app-btn-gloss px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RecentUserActivity() {
  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Recent User Activity
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track recent user logins and application events
        </p>
      </div>

      <UserLoginHistorySection />
      <EventHistorySection />
    </div>
  );
}

export { RecentUserActivity };
